import { useId, useRef } from "react";
import { createPortal } from "react-dom";
import useModalLayer from "./useModalLayer";

function joinClassNames(...values) {
  return values.filter(Boolean).join(" ");
}

export default function ModalSurface({
  kind,
  open,
  onClose,
  title,
  description,
  children,
  footer,
  initialFocusRef,
  dismissible = true,
  showCloseButton = true,
  closeLabel,
  className = "",
  surfaceClassName = "",
  portalTarget,
  "aria-label": ariaLabel,
  ...surfaceProps
}) {
  const surfaceRef = useRef(null);
  const generatedId = useId().replace(/:/g, "");
  const titleId = title ? `lz-v2-${kind}-title-${generatedId}` : undefined;
  const descriptionId = description
    ? `lz-v2-${kind}-description-${generatedId}`
    : undefined;

  useModalLayer({
    open,
    onClose: dismissible ? onClose : undefined,
    surfaceRef,
    initialFocusRef,
  });

  if (!open || typeof document === "undefined") {
    return null;
  }

  function handleBackdropPointerDown(event) {
    if (dismissible && event.target === event.currentTarget) {
      onClose?.("backdrop");
    }
  }

  const label = closeLabel || `Close ${kind}`;
  const target = portalTarget || document.body;

  return createPortal(
    <div
      className={joinClassNames(
        "lz-v2",
        "lz-v2-overlay",
        `lz-v2-overlay--${kind}`,
        className
      )}
      data-design-version="v2"
      onPointerDown={handleBackdropPointerDown}
    >
      <section
        {...surfaceProps}
        ref={surfaceRef}
        className={joinClassNames(
          "lz-v2-modal-surface",
          `lz-v2-${kind}`,
          surfaceClassName
        )}
        role="dialog"
        aria-modal="true"
        aria-label={title ? undefined : ariaLabel}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
      >
        {title || showCloseButton ? (
          <header className="lz-v2-modal-surface__header">
            {title ? (
              <h2 className="lz-v2-modal-surface__title" id={titleId}>{title}</h2>
            ) : <span />}
            {showCloseButton ? (
              <button
                type="button"
                className="lz-v2-modal-surface__close"
                aria-label={label}
                onClick={() => onClose?.("close-button")}
              >
                <span aria-hidden="true">×</span>
              </button>
            ) : null}
          </header>
        ) : null}
        {description ? (
          <p className="lz-v2-modal-surface__description" id={descriptionId}>
            {description}
          </p>
        ) : null}
        <div className="lz-v2-modal-surface__body">{children}</div>
        {footer ? <footer className="lz-v2-modal-surface__footer">{footer}</footer> : null}
      </section>
    </div>,
    target
  );
}
